import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Save, SlidersHorizontal, RotateCcw } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../lib/api";
import { calculate, round2 } from "../lib/calc";
import { loadDefaults, saveDefaults } from "../lib/storage";
import { loadHandoff, prefillFields } from "../lib/handoff";
import { CalcForm } from "../components/CalcForm";
import { ResultsPanel, MobileHud } from "../components/ResultsPanel";
import { SendQuoteButton } from "../components/SendQuoteButton";

const BLANK = {
  offer: "", loaded_miles: "", deadhead_miles: "", return_miles: "",
  fuel_price: "", loaded_mpg: "", empty_mpg: "", maintenance_cpm: "0.18",
  driver_mode: "flat", driver_flat: "", driver_rate: "", driver_hours: "",
  tolls: "", permits: "", hotel: "", meals: "", parking: "", other: "",
  margin_choice: 15, custom_margin: "",
};

export default function CalculatorPage() {
  const { user } = useAuth();
  const [handoff] = useState(loadHandoff);
  const [template, setTemplate] = useState("");
  const initial = () => ({ ...BLANK, ...(loadDefaults(user.id) || {}) });
  const [f, setF] = useState(() => ({ ...initial(), ...prefillFields(handoff) }));
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const r = useMemo(() => calculate(f), [f]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!handoff.haul_id) return;
    api.get("/config/public").then((res) => setTemplate(res.data.bid_url_template)).catch(() => {});
  }, [handoff.haul_id]);

  const canSave = r.status !== null && r.profit !== null && !saving;

  const onSave = async () => {
    setSaving(true);
    try {
      await api.post("/results", {
        label: label.trim() || null,
        true_trip_cost: round2(r.trueCost),
        recommended_quote: round2(r.recommendedQuote),
        projected_profit: round2(r.profit),
        status: r.status,
      });
      toast.success("Result saved to history");
      setLabel("");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const onDefaults = () => {
    saveDefaults(user.id, f);
    toast.success("Defaults saved on this device");
  };

  return (
    <div className="calc" data-testid="calculator-page">
      <MobileHud r={r} />
      <div className="calc-cols">
        <CalcForm f={f} set={set} marginInvalid={f.margin_choice === "custom" && r.margin === null} />
        <aside className="calc-side">
          <ResultsPanel r={r}>
            <SendQuoteButton haulId={handoff.haul_id} r={r} template={template} />
            <div className="save-box">
              <input className="label-input" data-testid="input-result-label" placeholder="Label (optional) — e.g. Sea Ray to Tampa" maxLength={120} value={label} onChange={(e) => setLabel(e.target.value)} />
              <button type="button" className="btn primary" data-testid="btn-save-result" disabled={!canSave} onClick={onSave}>
                <Save size={18} /> {saving ? "Saving…" : "Save result"}
              </button>
              <p className="save-note">Only status, cost, quote & profit are saved. Inputs never leave this device.</p>
            </div>
            <div className="row-btns">
              <button type="button" className="btn ghost" data-testid="btn-save-defaults" onClick={onDefaults}><SlidersHorizontal size={16} /> Save as my defaults</button>
              <button type="button" className="btn ghost" data-testid="btn-reset-form" onClick={() => setF(initial())}><RotateCcw size={16} /> Reset</button>
            </div>
          </ResultsPanel>
        </aside>
      </div>
    </div>
  );
}
